/**
 * Module dependencies.
 */

import { Image, Type } from 'src/components/core';
import { ReactElement } from 'react';
import { media, units, useBreakpoint } from '@untile/react-components';
import styled from 'styled-components';

/**
 * `Props` type.
 */

type Props = {
  className?: string;
  imageUrl: string;
  label: string;
  title: string;
};

/**
 * `StyledImage` styled component.
 */

const StyledImage = styled(Image)`
  transition: transform 0.3s ease;
`;

/**
 * `TextWrapper` styled component.
 */

const TextWrapper = styled.div`
  display: grid;
  grid-template-rows: repeat(2, max-content);
  padding: 0 ${units(1)};
  transition: transform 0.4s ease;

  ${media.min('md')`
    padding: 0 ${units(2)};
    grid-row-gap: ${units(1)};
  `}
`;

/**
 * `Wrapper` styled component.
 */

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  position: relative;

  &:focus,
  &:hover {
    ${StyledImage} {
      transform: scale(0.98);
    }

    ${TextWrapper} {
      transform: scale(0.965);
    }
  }
`;

/**
 * `ImageWrapper` styled component.
 */

const ImageWrapper = styled.div`
  margin-bottom: ${units(1)};
  overflow: hidden;
  padding-bottom: 100%;
  position: relative;

  ${media.min('md')`
    margin-bottom: ${units(2)};
  `}
`;

/**
 * `AwardCard` component.
 */

const AwardCard = (props: Props): ReactElement => {
  const { className, imageUrl, label, title } = props;
  const isMobile = useBreakpoint('ms', 'max');

  return (
    <Wrapper className={className}>
      <ImageWrapper>
        <StyledImage
          alt={title}
          fill
          src={imageUrl}
          style={{ objectFit: 'cover' }}
        />
      </ImageWrapper>

      <TextWrapper>
        {isMobile ? (
          <>
            <Type.H5>{title}</Type.H5>

            <Type.Small>{label}</Type.Small>
          </>
        ) : (
          <>
            <Type.H4>{title}</Type.H4>

            <Type.Paragraph>{label}</Type.Paragraph>
          </>
        )}
      </TextWrapper>
    </Wrapper>
  );
};

/**
 * Export `AwardCard` component.
 */

export default AwardCard;
